import type { Collector } from './index.h'

import { metricViews } from '@bldsoft/cappie/otel'
import merr from '@bldsoft/merr'
import { AggregationTemporalityPreference } from '@opentelemetry/exporter-metrics-otlp-http'
import { resourceFromAttributes } from '@opentelemetry/resources'
import {
  AggregationType,
  MeterProvider,
  PeriodicExportingMetricReader,
} from '@opentelemetry/sdk-metrics'
import { fullVersion, runtime } from '~/app/environment'
import { Logger } from '~/shared/logger'
import { OTLPMetricExporterOnlyWhenNeeded } from './exporter'

export const logger = new Logger('tria/opentelemetry')

/**
 * Initialize OpenTelemetry metrics
 */
export function initTelemetry(collector: Collector): MeterProvider {
  // diag.setLogger(new DiagConsoleLogger(), DiagLogLevel.DEBUG)

  const exporter = new OTLPMetricExporterOnlyWhenNeeded({
    url: collector.url,
    headers: collector.headers || {}, // always set headers, in that case delegate will choose xhr instead of beacon
    // concurrencyLimit: 1,
    temporalityPreference: AggregationTemporalityPreference.DELTA,
  })

  const resource = resourceFromAttributes({
    'service.version': fullVersion, // == `app_releaseVersion`
    'service.name': runtime.app.name, // == `app_name`, got from era, e.g. 'tria',
    'service.instance.id': runtime.app.domain, // == `app_id`

    // 'device.id': session?.deviceIdentifier || '', // == `device_id`
    'device.type': 'web', // == `device_type`
    'device.manufacturer': runtime.os.name || 'Unknown', // == `device_name`
    'device.model.name': runtime.browser.name || 'Unknown', // == `device_model`
    'os.version': runtime.browser.version || '-', // == `device_OsVersion`

    // 'net.host.ip': session?.deviceExternalIP || '', // == `network_ip`
    // 'net.host.carrier.isp': session?.deviceISP || '', // == `network_isp`
    // 'net.host.carrier.location': session?.location || '', // == `network_location`
    // 'setplex.provider.id': session?.providerId || '', // == `content_provider_id`
    // 'setplex.user.subscriber.id': session?.subscriberId || '', // == `content_user_subscriber_id`
    // 'setplex.user.account.number': session?.accountNumber || '', // == `content_user_account_number`
    // 'setplex.user.profile.id': session?.profileId ? String(session?.profileId) : '', // == `content_user_profile_id`
    // 'setplex.user.profile.name': session?.subscriberName || '', // == `content_user_profile_name`
  })

  //TODO dynamically update attributes
  // sessionModel.$session.watch((session) => {
  //   resource._memoizedAttributes = null
  //   resource._rawAttributes = ...
  // })

  const meterProvider = new MeterProvider({
    resource,
    views: metricViews().map((spec) => ({
      instrumentName: spec.instrumentName,
      aggregation: {
        type: AggregationType.EXPLICIT_BUCKET_HISTOGRAM,
        options: { boundaries: spec.boundaries },
      },
    })),
    readers: [
      new PeriodicExportingMetricReader({
        exporter,
        exportIntervalMillis: 10000, // export metrics every 10 seconds
      }),
    ],
  })

  // metrics.setGlobalMeterProvider(meterProvider)

  return meterProvider
}

/**
 * Halt OpenTelemetry metrics
 */
export async function haltTelemetry(
  meterProvider?: MeterProvider | null
): Promise<void> {
  if (meterProvider == null) return

  // meterProvider.forceFlush() // should do this?
  await meterProvider.shutdown()
}

/**
 * Parse and initial validation of collector URL
 */
export function parseCollectorUrl(url?: string | null): Collector | null {
  // check if url is set
  // if url starts with #, it is considered as empty value (like comment)
  if (!url || typeof url !== 'string' || url.startsWith('#')) {
    return null
  }

  // just in case, trim url
  url = url.trim()

  // check if url starts with http or https
  if (!/^https?:\/\//i.test(url)) {
    return {
      url,
      valid: false,
      error: 'Only HTTP or HTTPS URLs are supported',
    }
  }

  // parse url, this will fail on invalid URL
  try {
    const parsed = new URL(url)
    let headers: Record<string, string> | undefined

    // if there are any credentials in the url - cut them and transform to headers
    if (parsed.username || parsed.password) {
      headers = {
        Authorization: `Basic ${btoa(`${parsed.username}:${parsed.password}`)}`,
      }
      parsed.username = ''
      parsed.password = ''
    }

    return {
      url: parsed.toString(),
      headers,
      valid: true,
    }
  } catch (e) {
    return {
      url,
      valid: false,
      error: String(e),
    }
  }
}

/**
 * Validates OpenTelemetry collector URL,
 * by actually sending a minimal valid request to the collector
 */
export async function verifyCollector(collector: Collector): Promise<void> {
  const res = await merr(collector.url, {
    method: 'POST',
    headers: collector.headers,
    json: {
      resourceMetrics: [],
    },
  })

  if (!res.ok) {
    throw new Error('Invalid response from OpenTelemetry collector')
  }
}
